import { fetchDELETEWithAuth, fetchGET, fetchPOSTWithAuth } from "@/utils/api-utils";
import {
  BlogPost,
  CaseStudy,
  ChangelogEntry,
  CompetitorPost,
  CreateBlogRequest,
  CreateCaseStudyRequest,
  CreateChangelogRequest,
  CreateCompetitorRequest,
} from "./types";

/**
 * Publications API service for managing blogs, case studies, and competitor posts
 */

// Blog API functions
export async function fetchBlogs(): Promise<BlogPost[]> {
  const response = await fetchGET("/posts/blog");

  if (!response.ok) {
    throw new Error(`Failed to fetch blogs: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();
  return result.data;
}

export async function createBlog(blogData: CreateBlogRequest): Promise<BlogPost> {
  const response = await fetchPOSTWithAuth("/posts/blog", blogData);

  if (!response.ok) {
    throw new Error(`Failed to create blog: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();
  return result.data;
}

export async function updateBlog(blogId: number, blogData: CreateBlogRequest): Promise<BlogPost> {
  const response = await fetchPOSTWithAuth(`/posts/blog?postId=${blogId}`, blogData);

  if (!response.ok) {
    throw new Error(`Failed to update blog: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();
  return result.data;
}

export async function deleteBlog(postId: number): Promise<void> {
  const response = await fetchDELETEWithAuth(`/posts/blog?postId=${postId}`);

  if (!response.ok) {
    throw new Error(`Failed to delete blog: ${response.status} ${response.statusText}`);
  }
}

// Case Study API functions
export async function fetchCaseStudies(): Promise<CaseStudy[]> {
  const response = await fetchGET("/posts/case-study");

  if (!response.ok) {
    throw new Error(`Failed to fetch case studies: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();
  return result.data;
}

export async function createCaseStudy(caseStudyData: CreateCaseStudyRequest): Promise<CaseStudy> {
  const response = await fetchPOSTWithAuth("/posts/case-study", caseStudyData);

  if (!response.ok) {
    let errorMessage = `Failed to create case study: ${response.status} ${response.statusText}`;

    // Try to parse error details from response
    try {
      const errorData = await response.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // If parsing fails, use the default message
    }

    throw new Error(errorMessage);
  }

  const result = await response.json();
  return result.data;
}

export async function updateCaseStudy(caseStudyId: number, caseStudyData: CreateCaseStudyRequest): Promise<CaseStudy> {
  const response = await fetchPOSTWithAuth(`/posts/case-study?postId=${caseStudyId}`, caseStudyData);

  if (!response.ok) {
    let errorMessage = `Failed to update case study: ${response.status} ${response.statusText}`;

    // Try to parse error details from response
    try {
      const errorData = await response.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // If parsing fails, use the default message
    }

    throw new Error(errorMessage);
  }

  const result = await response.json();
  return result.data;
}

export async function deleteCaseStudy(postId: number): Promise<void> {
  const response = await fetchDELETEWithAuth(`/posts/case-study?postId=${postId}`);

  if (!response.ok) {
    throw new Error(`Failed to delete case study: ${response.status} ${response.statusText}`);
  }
}

// Competitor API functions
export async function fetchCompetitors(): Promise<CompetitorPost[]> {
  const response = await fetchGET("/posts/competitor");

  if (!response.ok) {
    throw new Error(`Failed to fetch competitors: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();
  return result.data;
}

export async function createCompetitor(competitorData: CreateCompetitorRequest): Promise<CompetitorPost> {
  const response = await fetchPOSTWithAuth("/posts/competitor", competitorData);

  if (!response.ok) {
    throw new Error(`Failed to create competitor: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();
  return result.data;
}

export async function updateCompetitor(
  postId: number,
  competitorData: CreateCompetitorRequest
): Promise<CompetitorPost> {
  const response = await fetchPOSTWithAuth(`/posts/competitor?postId=${postId}`, competitorData);

  if (!response.ok) {
    throw new Error(`Failed to update competitor: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();
  return result.data;
}

export async function deleteCompetitor(postId: number): Promise<void> {
  const response = await fetchDELETEWithAuth(`/posts/competitor?postId=${postId}`);

  if (!response.ok) {
    throw new Error(`Failed to delete competitor: ${response.status} ${response.statusText}`);
  }
}

// Changelog API functions
export async function fetchChangelogs(): Promise<ChangelogEntry[]> {
  const response = await fetchGET("/posts/change-logs");

  if (!response.ok) {
    throw new Error(`Failed to fetch changelogs: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();
  return result.data;
}

export async function createChangelog(changelog: CreateChangelogRequest): Promise<ChangelogEntry> {
  const response = await fetchPOSTWithAuth("/posts/change-logs", changelog);

  if (!response.ok) {
    throw new Error(`Failed to create changelog: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();
  return result.data;
}

export async function updateChangelog(
  postId: number,
  changelog: Partial<CreateChangelogRequest>
): Promise<ChangelogEntry> {
  const response = await fetchPOSTWithAuth(`/posts/change-logs?postId=${postId}`, changelog);

  if (!response.ok) {
    throw new Error(`Failed to update changelog: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();
  return result.data;
}

export async function deleteChangelog(postId: number): Promise<void> {
  const response = await fetchDELETEWithAuth(`/posts/change-logs?postId=${postId}`);

  if (!response.ok) {
    throw new Error(`Failed to delete changelog: ${response.status} ${response.statusText}`);
  }
}
